<script lang="ts">
    import type {PublicStatsData, PublicStatsDataParams} from '../types';
    import type {EditedProperty} from 'typings/portaro.be.types';
    import type {Subscription} from 'rxjs';
    import {filterPublicStatsParams} from '../utils';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import {StatsService} from '../stats.service';
    import {onDestroy, onMount} from 'svelte';
    import {fly} from 'svelte/transition';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {transferify} from 'shared/utils/data-service-utils';
    import {isEmpty, isEqual} from 'lodash-es';
    import KpHorizontalSeparator from 'shared/ui-widgets/separator/KpHorizontalSeparator.svelte';
    import StatsHeadingPart from '../shared-parts/StatsHeadingPart.svelte';
    import PublicStatsDiskUsageInfoPart from './parts/PublicStatsDiskUsageInfoPart.svelte';
    import PublicStatsGeneralInfoPart from './parts/PublicStatsGeneralInfoPart.svelte';
    import PublicStatsFondInfoPart from './parts/PublicStatsFondInfoPart.svelte';
    import Checkbox from 'shared/ui-widgets/checkbox/Checkbox.svelte';
    import PublicStatsSeekerPart from './parts/PublicStatsSeekerPart.svelte';
    import PublicStatsTextualInfoPart from './parts/PublicStatsTextualInfoPart.svelte';
    import PublicStatsSearchesGraphPart from './parts/PublicStatsSearchesGraphPart.svelte';
    import PublicStatsEntersGraphPart from './parts/PublicStatsEntersGraphPart.svelte';
    import PublicStatsLoginsAndEntersGraphPart from './parts/PublicStatsLoginsAndEntersGraphPart.svelte';
    import PublicStatsMovementsGraphPart from './parts/PublicStatsMovementsGraphPart.svelte';
    import KpStatsPageContainer from '../components/KpStatsPageContainer.svelte';

    const service = getInjector().getByToken<StatsService>(StatsService.serviceName);
    const infoFlyInParams = {y: 15, duration: 375};
    const dateFormatter = getDateFormatter();
    const localize = getLocalization();

    let loading = true;
    let statsData: PublicStatsData;
    let editorFields: EditedProperty[];
    let params: PublicStatsDataParams;
    let paramsSubscription: Subscription;

    let textualVersion = false;
    let printMode = false;
    $: showingTextualVersion = textualVersion || printMode;

    onMount(async () => {
        editorFields = await service.loadPublicStatsFormSettings();
        statsData = await service.loadPublicStatsData();

        if (!exists(statsData) || !exists(editorFields)) {
            loading = false;
            return;
        }

        params = filterPublicStatsParams(statsData.stats.params);
        paramsSubscription = service.getPublicStatsParamsState().subscribe((changedParams) => {
            if (isEmpty(changedParams)) {
                return;
            }

            if (!isEqual(changedParams, transferify(params))) {
                params = changedParams;
                handleSearchWithNewParams();
            }
        });

        loading = false;
    });

    onDestroy(() => {
        if (exists(paramsSubscription)) {
            paramsSubscription.unsubscribe();
        }
    });

    const handleSearchWithNewParams = async () => {
        loading = true;
        statsData = await service.loadPublicStatsData(params);

        if (!exists(statsData)) {
            return;
        }

        params = filterPublicStatsParams(statsData.stats.params);
        service.requestUrlParamsChange(params);
        loading = false;
    }
</script>

<KpStatsPageContainer pageClass="statistiky"
                      loadError="{!loading && (!exists(statsData) || !exists(editorFields))}"
                      loadingLabel="{localize(/* @kp-localization statistiky.PublicStatsLoading */ 'statistiky.PublicStatsLoading')}"
                      loadErrorLabel="{localize(/* @kp-localization statistiky.PublicStatsLoadError */ 'statistiky.PublicStatsLoadError')}"
                      {loading}
                      bind:printMode
                      let:handlePrint>

    <StatsHeadingPart {printMode} on:print={handlePrint}>
        {localize(/* @kp-localization statistiky.STATISTIKY */ 'statistiky.STATISTIKY')}
    </StatsHeadingPart>

    {#if !printMode}
        <PublicStatsSeekerPart {statsData}
                               {editorFields}
                               bind:params
                               on:search={handleSearchWithNewParams}/>
    {/if}

    <h2>
        {localize(/* @kp-localization statistiky.SummaryRange */ 'statistiky.SummaryRange')}:
        {pipe(statsData.stats.params.fromDate, dateFormatter('d.M.yyyy HH:mm'))} - {pipe(statsData.stats.params.toDate, dateFormatter('d.M.yyyy HH:mm'))}
    </h2>

    <div class="graphs-row" class:side-by-side={printMode}>
        <PublicStatsSearchesGraphPart {statsData}/>
        <PublicStatsEntersGraphPart {statsData}/>
    </div>

    <PublicStatsMovementsGraphPart {statsData}/>
    <PublicStatsLoginsAndEntersGraphPart {statsData}/>

    <KpHorizontalSeparator width="100%"/>
    <div class="info-section-heading-container">
        <h2>
            {localize(/* @kp-localization statistiky.Souhrny */ 'statistiky.Souhrny')}
        </h2>

        {#if !printMode}
            <div class="textual-version-container">
                <label for="textual-version-checkbox"
                       class="text-muted">{localize(/* @kp-localization statistiky.TextualVersionCheckboxLabel */ 'statistiky.TextualVersionCheckboxLabel')}:</label>
                <Checkbox id="textual-version-checkbox" bind:checked={textualVersion}/>
            </div>
        {/if}
    </div>

    {#key showingTextualVersion}
        <div class="info-container" in:fly={printMode ? {y: 0, duration: 0} : infoFlyInParams}>
            {#if showingTextualVersion}
                <PublicStatsTextualInfoPart {statsData}/>
            {:else}
                <PublicStatsGeneralInfoPart {statsData}/>
                <PublicStatsFondInfoPart {statsData}/>
                {#if service.isCurrentUserLibrarian()}
                    <PublicStatsDiskUsageInfoPart {statsData}/>
                {/if}
            {/if}
        </div>
    {/key}
</KpStatsPageContainer>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.media-queries.less";

    h2 {
        width: 100%;
        margin: 0;
    }

    .graphs-row {
        display: grid;
        grid-template-columns: 1fr;
        gap: @spacing-l;
        width: 100%;

        @media (min-width: @screen-sm-min) {
            grid-template-columns: repeat(2, 1fr);
        }

        &.side-by-side {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    .info-section-heading-container {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .textual-version-container {
            display: flex;
            align-items: center;
            gap: @spacing-s;

            label {
                user-select: none;
                cursor: pointer;
                text-wrap: nowrap;
                margin: 0;
            }
        }
    }

    .info-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 36px;
    }
</style>